import { useCallback, useState } from "react";
import { useApi } from "../apiCall/useApi";
import Joi from "joi";
import { Urls } from "../../../utils/constant";
import { size } from "lodash";
import { useSnackbar } from "notistack";

import {
  hidePageLoader,
  showPageLoader,
} from "../../reducers/Common/PageLoader/PageLoader.reducer";
import { useDispatch, useSelector } from "react-redux";

import useFullPageLoader from "../Common/PageLoader/useFullPageLoader";
import {
  setAreaComboList,
  setDoctorAvailabilityData,
  setDoctorComboList,
  setWeekDayList,
} from "../../reducers/DoctorAvailability/DoctorAvailabilityForm.reducer";
import { format } from "date-fns";
import dayjs from "dayjs";
import {
  getWeekDayList,
  getDoctorList,
  getAreaList,
} from "../../selectors/DoctorAvailability/doctorAvailabilityForm.selector";

export const DoctorAvailabilityEntrySchema = Joi.object({
  name: Joi.string().required().messages({
    "string.empty": "Please Select a Doctor Name ",
  }),
  area_name: Joi.string().required().messages({
    "string.empty": "Please Select a Area Name ",
  }),

  week_day: Joi.string().required().messages({
    "string.empty": "Week Day is required.",
  }),

  // times are optional here; we enforce requirements in custom()
  morning_from_time: Joi.string().allow("", null),
  morning_upto_time: Joi.string().allow("", null),
  evening_from_time: Joi.string().allow("", null),
  evening_upto_time: Joi.string().allow("", null),
});

export function useDoctorAvailabilityForm({
  existingWeekDay,
  existingDoctorName,
  existingAreaName,
  existingMorningFromTime,
}) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { apiPost } = useApi();
  const [loading, setLoading] = useState(false);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const doctorList = useSelector(getDoctorList);
  const areaList = useSelector(getAreaList);
  const [weekDay, setWeekDay] = useState(existingWeekDay);
  const [DoctorId, setDoctorId] = useState(existingDoctorName);
  const [AreaId, setAreaId] = useState(existingAreaName);
  const [Id, setId] = useState("");
  const [MorningFromTime, setMorningFromTime] = useState("");
  const [MorningUptoTime, setMorningUptoTime] = useState("");
  const [EveningFromTime, setEveningFromTime] = useState("");
  const [EveningUptoTime, setEveningUptoTime] = useState("");
  const [areaWarning, setAreaWarning] = useState(null);

  const handleDeleteId = useCallback((row) => {
    setId(row.id);
    setDoctorId(row.doctor_id);
    setAreaId(row.area_id);
    setWeekDay(row.week_day);
    setMorningFromTime(row.morning_from_time);
    setMorningUptoTime(row.morning_upto_time);
    setEveningFromTime(row.evening_from_time);
    setEveningUptoTime(row.evening_upto_time);
  }, []);

  const DoctorAvailabilityEntry = useCallback(
    async ({
      morning_from_time,
      morning_upto_time,
      evening_from_time,
      evening_upto_time,
    }) => {
      // if (loading) return;
      // setLoading(true);
      try {
        dispatch(showPageLoader());
        const formattedMorningFromTime = morning_from_time
          ? dayjs(morning_from_time, "HH:mm:ss", true).isValid()
            ? dayjs(morning_from_time, "HH:mm:ss").format("HH:mm")
            : "Invalid Time"
          : "";
        const formattedMorningUptoTime = morning_upto_time
          ? dayjs(morning_upto_time, "HH:mm:ss", true).isValid()
            ? dayjs(morning_upto_time, "HH:mm:ss").format("HH:mm")
            : "Invalid Time"
          : "";
        const formattedEveningFromTime = evening_from_time
          ? dayjs(evening_from_time, "HH:mm:ss", true).isValid()
            ? dayjs(evening_from_time, "HH:mm:ss").format("HH:mm")
            : "Invalid Time"
          : "";
        const formattedEveningUptoTime = evening_upto_time
          ? dayjs(evening_upto_time, "HH:mm:ss", true).isValid()
            ? dayjs(evening_upto_time, "HH:mm:ss").format("HH:mm")
            : "Invalid Time"
          : "";

        const { data } = await apiPost({
          url: Urls.doctorAvailabilityAdd(),
          data: {
            doctor_id: DoctorId,
            area_id: AreaId,
            week_day: weekDay,
            morning_from_time: formattedMorningFromTime,
            morning_upto_time: formattedMorningUptoTime,
            evening_from_time: formattedEveningFromTime,
            evening_upto_time: formattedEveningUptoTime,
            insert_by: "",
          },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        if (data.status === 202) {
          enqueueSnackbar("This doctor is already assigned to this week day.", {
            variant: "error",
          });
          return false;
        }

        // Backend returned a message (e.g. area not mapped to doctor)
        if (data.message) {
          setAreaWarning({ message: data.message });
          setLoading(false);
          dispatch(hidePageLoader());
          return false;
        }

        if (size(data.id) === 36) {
          enqueueSnackbar("Availability Data Added Successfully.", {
            variant: "success",
          });
        }

        setLoading(false);
        dispatch(hidePageLoader());
        return true;
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        enqueueSnackbar("Problem While Saving Availability Data", {
          variant: "error",
        });
        return false;
      }
    },
    [dispatch, apiPost, DoctorId, AreaId, weekDay, enqueueSnackbar]
  );

  const DoctorAvailabilityUpdate = useCallback(
    async ({
      id,
      morning_from_time,
      morning_upto_time,
      evening_from_time,
      evening_upto_time,
    }) => {
      if (loading) return;
      setLoading(true);
      try {
        dispatch(showPageLoader());

        const formattedMorningFromTime = morning_from_time
          ? dayjs(morning_from_time, "HH:mm:ss", true).isValid()
            ? dayjs(morning_from_time, "HH:mm:ss").format("HH:mm")
            : "Invalid Time"
          : "";
        const formattedMorningUptoTime = morning_upto_time
          ? dayjs(morning_upto_time, "HH:mm:ss", true).isValid()
            ? dayjs(morning_upto_time, "HH:mm:ss").format("HH:mm")
            : "Invalid Time"
          : "";
        const formattedEveningFromTime = evening_from_time
          ? dayjs(evening_from_time, "HH:mm:ss", true).isValid()
            ? dayjs(evening_from_time, "HH:mm:ss").format("HH:mm")
            : "Invalid Time"
          : "";
        const formattedEveningUptoTime = evening_upto_time
          ? dayjs(evening_upto_time, "HH:mm:ss", true).isValid()
            ? dayjs(evening_upto_time, "HH:mm:ss").format("HH:mm")
            : "Invalid Time"
          : "";

        const { data } = await apiPost({
          url: Urls.doctorAvailabilityUpdate(),
          data: {
            id: id,
            doctor_id: DoctorId,
            area_id: AreaId,
            week_day: weekDay,
            morning_from_time: formattedMorningFromTime,
            morning_upto_time: formattedMorningUptoTime,
            evening_from_time: formattedEveningFromTime,
            evening_upto_time: formattedEveningUptoTime,
            update_by: "",
          },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        // Backend returned a message (e.g. area not mapped to doctor)
        if (data.message) {
          setAreaWarning({ message: data.message });
          setLoading(false);
          dispatch(hidePageLoader());
          return false;
        }

        if (size(data.id) === 36) {
          enqueueSnackbar("Availability Data Updated Successfully.", {
            variant: "success",
          });
          return true;
        }

        setLoading(false);
        dispatch(hidePageLoader());
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        enqueueSnackbar("Problem While Updating Availability Data", {
          variant: "error",
        });
        return false;
      }
    },
    [loading, dispatch, apiPost, DoctorId, AreaId, weekDay, enqueueSnackbar]
  );

  const DoctorAvailabilityDelete = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      dispatch(showPageLoader());

      const formattedMorningFromTime = MorningFromTime
        ? dayjs(MorningFromTime, "HH:mm:ss", true).isValid()
          ? dayjs(MorningFromTime, "HH:mm:ss").format("HH:mm")
          : "Invalid Time"
        : "";
      const formattedMorningUptoTime = MorningUptoTime
        ? dayjs(MorningUptoTime, "HH:mm:ss", true).isValid()
          ? dayjs(MorningUptoTime, "HH:mm:ss").format("HH:mm")
          : "Invalid Time"
        : "";
      const formattedEveningFromTime = EveningFromTime
        ? dayjs(EveningFromTime, "HH:mm:ss", true).isValid()
          ? dayjs(EveningFromTime, "HH:mm:ss").format("HH:mm")
          : "Invalid Time"
        : "";
      const formattedEveningUptoTime = EveningUptoTime
        ? dayjs(EveningUptoTime, "HH:mm:ss", true).isValid()
          ? dayjs(EveningUptoTime, "HH:mm:ss").format("HH:mm")
          : "Invalid Time"
        : "";

      const weekDayVal = weekDay === "All" ? null : weekDay;

      const { data } = await apiPost({
        url: Urls.doctorAvailabilityUpdate(),
        data: {
          id: Id,
          doctor_id: DoctorId,
          week_day: weekDayVal,
          morning_from_time: formattedMorningFromTime,
          morning_upto_time: formattedMorningUptoTime,
          evening_from_time: formattedEveningFromTime,
          evening_upto_time: formattedEveningUptoTime,
          update_by: "",
        },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (size(data.id) === 36) {
        enqueueSnackbar("Availability Data Deleted Successfully.", {
          variant: "success",
        });
        return true;
      }

      setLoading(false);
      dispatch(hidePageLoader());
    } catch (err) {
      dispatch(hidePageLoader());
      console.log(err);
      setLoading(false);
      return false;
    }
  }, [
    loading,
    dispatch,
    MorningFromTime,
    MorningUptoTime,
    EveningFromTime,
    EveningUptoTime,
    weekDay,
    apiPost,
    Id,
    DoctorId,
    enqueueSnackbar,
  ]);

  const fetchDoctorComboList = useCallback(async () => {
    try {
      const { data } = await apiPost({
        url: Urls.getDoctorCombo(),
        data: {},
      });
      // setting list to reducer

      dispatch(setDoctorComboList(data.data));
    } catch (err) {
      console.log(err);
    }
  }, [apiPost, dispatch]);

  const findDoctorFromLabel = useCallback(
    (value) => {
      const selectedDoctorId = value
        ? doctorList.find((item) => item.label === value)?.id
        : null;

      setDoctorId(selectedDoctorId);
      fetchWeekDayList(selectedDoctorId);
      return selectedDoctorId;
    },
    [doctorList]
  );

  const fetchAreaComboList = useCallback(async () => {
    try {
      const { data } = await apiPost({
        url: Urls.getAreaCombo(),
        data: {},
      });
      // setting list to reducer

      dispatch(setAreaComboList(data));
    } catch (err) {
      console.log(err);
    }
  }, [apiPost, dispatch]);

  const findAreaFromLabel = useCallback(
    (value) => {
      const selectedAreaId = value
        ? areaList.find((item) => item.label === value)?.id
        : null;

      setAreaId(selectedAreaId);
      fetchWeekDayList(selectedAreaId);
      return selectedAreaId;
    },
    [areaList]
  );

  const getDoctorAvailabilityById = useCallback(
    async (id) => {
      if (loading) return;
      setLoading(true);
      try {
        dispatch(showPageLoader());

        const { data } = await apiPost({
          url: Urls.getDoctorAvailabilityById(),
          data: {
            id: id,
          },
        });

        dispatch(setDoctorAvailabilityData(data));

        setLoading(false);
        dispatch(hidePageLoader());
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        return false;
      }
    },
    [apiPost, dispatch, loading]
  );

  const fetchWeekDayList = useCallback(
    async (id) => {
      try {
        dispatch(showPageLoader());
        const { data } = await apiPost({
          url: Urls.getWeekDayCombo(),
          data: {},
        });
        dispatch(setWeekDayList(data));

        dispatch(hidePageLoader());
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
      }
    },
    [apiPost, dispatch]
  );

  const handleWeekDayChange = useCallback((value) => {
    setWeekDay(value);
  }, []);

  // const handleDateChange = (newValue) => {
  //   setMorningFromTime(newValue ? newValue.format("HH:mm") : null);
  // };

  return {
    loader,
    loading,
    DoctorAvailabilityEntrySchema,
    handleWeekDayChange,
    // handleDateChange,
    DoctorId,
    AreaId,
    weekDay,
    DoctorAvailabilityUpdate,
    DoctorAvailabilityEntry,
    findDoctorFromLabel,
    getDoctorAvailabilityById,
    handleDeleteId,
    DoctorAvailabilityDelete,
    fetchWeekDayList,
    fetchDoctorComboList,
    fetchAreaComboList,
    findAreaFromLabel,
    areaWarning,
    setAreaWarning,
  };
}
